import { Component, OnInit, AfterContentChecked, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LookupData, Tab_Title } from 'src/app/shared/models/lookup-data.model';
import { Router, ActivatedRoute } from '@angular/router';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { EnglishProficiencyDetails } from '../models/english-proficiency-details';
import { SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Action_ApplicationSectionSaved } from '../state/actions';
import { Observable, Subscription, merge } from 'rxjs';
import { ModalService } from 'src/app/shared/services/modal.service';
import { compareEnglishProficiencyDetails } from '../shared/utils/ModelUtils';
import { slideInOut } from '../animations/slideInOut';
import { FormService } from 'src/app/shared/services/form.service';
import { Title } from '@angular/platform-browser';
import * as _ from 'lodash';
import { debounceTime } from 'rxjs/operators';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'online-app-english-proficiency',
  templateUrl: './english-proficiency.component.html',
  styleUrls: ['./english-proficiency.component.css'],
  animations: [slideInOut],
  host: {
    class: 'application-section-component'
  },
})
export class EnglishProficiencyComponent implements OnInit, AfterContentChecked, OnDestroy {

  englishProficiencyFormGroup: FormGroup;
  isFormSubmitted = false;
  applicationId: number;
  testTypeCode = '';
  hasTakenTest: boolean;
  initialEnglishProficiencyDetails: EnglishProficiencyDetails;

  isSaveSuccessful = false;
  isScrollDone = false;
  cefrLevels: LookupData[];
  ieltsScores: LookupData[];
  isSubmittedApplication = false;
  averageIeltsScore: number;

  // selectedTestTypeCode = '';
  allSubjectsTaughtInEnglish: boolean;
  speaksEnglishAtHome: boolean;

  errorKeyFormFieldMapping = [
    { apiErrorKeyName: 'TestTypeCode', formFieldName: 'testTypeCode', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'HasTakenTest', formFieldName: 'hasTakenTest', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'DateTaken', formFieldName: 'dateTaken', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'ListeningScore', formFieldName: 'listeningScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'ReadingScore', formFieldName: 'readingScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'WritingScore', formFieldName: 'writingScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'SpeakingScore', formFieldName: 'speakingScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'VocabularyScore', formFieldName: 'vocabularyScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'TotalScore', formFieldName: 'totalScore', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'OtherTestName', formFieldName: 'otherTestName', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'AllSubjectsTaughtInEnglish', formFieldName: 'allSubjectsTaughtInEnglish', apiValidationErrorMsg: null },
    { apiErrorKeyName: 'SpeaksEnglishAtHome', formFieldName: 'speaksEnglishAtHome', apiValidationErrorMsg: null }
  ];

  private subscriptionsToDispose: Subscription[] = [];

  constructor(private fb: FormBuilder,
    private router: Router,
    private store: Store<any>,
    private modalService: ModalService,
    private formService: FormService,
    private applicationService: ApplicationService,
    private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService,
    private titleService: Title) {
    this.activatedRoute.parent.params.subscribe(params => {
      this.applicationId = +params['id'];
    });

    // this.configureJquery();
  }

  ngOnInit() {
    if (localStorage.getItem("isAgent") == "true") {
      this.titleService.setTitle(Tab_Title.Agent_EnglishProficiency_Title);
    } else {
      this.titleService.setTitle(Tab_Title.EnglishProficiency_Title);
    }
    this.englishProficiencyFormGroup = this.fb.group({
      testTypeCode: '',
      hasTakenTest: false,
      dateTaken: null,
      listeningScore: '',
      readingScore: '',
      writingScore: '',
      speakingScore: '',
      vocabularyScore: '',
      totalScore: '',
      otherTestName: '',
      allSubjectsTaughtInEnglish: '',
      speaksEnglishAtHome: ''
    });

    this.cefrLevels = [
      { Code: '', Description: 'Please select' },
      { Code: 'BelowA1', Description: 'Below A1' },
      { Code: 'A1', Description: 'A1' },
      { Code: 'A2', Description: 'A2' },
      { Code: 'B1', Description: 'B1' },
      { Code: 'B2', Description: 'B2' },
      { Code: 'C1', Description: 'C1' },
      { Code: 'C2', Description: 'C2' }
    ];

    this.ieltsScores = [
      { Code: '', Description: 'Please select' },
      { Code: '1.0', Description: '1.0' },
      { Code: '1.5', Description: '1.5' },
      { Code: '2.0', Description: '2.0' },
      { Code: '2.5', Description: '2.5' },
      { Code: '3.0', Description: '3.0' },
      { Code: '3.5', Description: '3.5' },
      { Code: '4.0', Description: '4.0' },
      { Code: '4.5', Description: '4.5' },
      { Code: '5.0', Description: '5.0' },
      { Code: '5.5', Description: '5.5' },
      { Code: '6.0', Description: '6.0' },
      { Code: '6.5', Description: '6.5' },
      { Code: '7.0', Description: '7.0' },
      { Code: '7.5', Description: '7.5' },
      { Code: '8.0', Description: '8.0' },
      { Code: '8.5', Description: '8.5' },
      { Code: '9.0', Description: '9.0' }
    ];

    this.subscriptionsToDispose.push(
      merge(
        this.englishProficiencyFormGroup.get('listeningScore').valueChanges
          .pipe(debounceTime(250)),
        this.englishProficiencyFormGroup.get('writingScore').valueChanges
          .pipe(debounceTime(250)),
        this.englishProficiencyFormGroup.get('speakingScore').valueChanges
          .pipe(debounceTime(250)),
        this.englishProficiencyFormGroup.get('readingScore').valueChanges
          .pipe(debounceTime(250)),
      ).subscribe(() => {
        this.calculateAverageScoreIelts(this.englishProficiencyFormGroup.get('listeningScore').value, 
          this.englishProficiencyFormGroup.get('writingScore').value, 
          this.englishProficiencyFormGroup.get('speakingScore').value, 
          this.englishProficiencyFormGroup.get('readingScore').value);
      })
    );

    this.applicationService.getEnglishProficiencyDetails(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          this.updateFormValuesFromEnglishProficiency(response.Result);
          this.initialEnglishProficiencyDetails = this.getEnglishProficiencyDetails();
        }
      }, err => {
        console.error(err);
      }, () => {
        // this.configureJquery();
      });

    this.applicationService.getApplicationDetails(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          this.isSubmittedApplication = response.Result.ApplicationStatus === 'Submitted';
          if (this.isSubmittedApplication) {
            this.englishProficiencyFormGroup.disable();
          }
        }
      }, err => {
        console.error(err);
      });
  }

  ngOnDestroy() {
    this.subscriptionsToDispose.forEach(s => s.unsubscribe());
  }

  calculateAverageScoreIelts(listeningScore: string, writingScore: string, speakingScore: string, readingScore: string): void {

    if (this.englishProficiencyFormGroup.get('testTypeCode').value !== 'IELTS')
      return;

    let totalScore = _.toNumber(listeningScore) + _.toNumber(writingScore) + _.toNumber(speakingScore) + _.toNumber(readingScore);
    let intScore = parseInt((totalScore / 4).toString(), 10);
    let decimalScore = (totalScore / 4) - intScore;
    this.averageIeltsScore = 0;
    switch (decimalScore) {
      case 0.125:
        this.averageIeltsScore = intScore;
        break;
      case 0.25:
        this.averageIeltsScore = intScore + 0.5;
        break;
      case 0.375:
        this.averageIeltsScore = intScore + 0.5;
        break;
      case 0.5:
        this.averageIeltsScore = totalScore / 4;
        break;
      case 0.625:
        this.averageIeltsScore = intScore + 0.5;
        break;
      case 0.75:
        this.averageIeltsScore = intScore + 1;
        break;
      case 0.875:
        this.averageIeltsScore = intScore + 1;
        break;
      default:
        this.averageIeltsScore = totalScore / 4;
        break;
    }
  }

  getDateInString(dateObject: Date): string {
    return `${dateObject.getDate()}/${dateObject.getMonth() + 1}/${dateObject.getFullYear()}`;
  }

  setDateOfTest(dateObject: Date) {
    this.englishProficiencyFormGroup.patchValue({
      dateTaken: dateObject,
    });
    this.englishProficiencyFormGroup.updateValueAndValidity();
  }

  isThisFieldHasErrors(fieldName: string): boolean {
    return this.isFormSubmitted && this.englishProficiencyFormGroup.get(fieldName).errors != null;
  }

  isThisFieldHasApiError(fieldName: string): boolean {
    return this.isFormSubmitted
      && this.englishProficiencyFormGroup.get(fieldName).errors != null
      && this.englishProficiencyFormGroup.get(fieldName).errors.apiValidationError != null;
  }

  updateTestTypeCode(testTypeCode: string) {
    this.testTypeCode = testTypeCode;

    this.englishProficiencyFormGroup.patchValue({
      testTypeCode: testTypeCode,
      dateTaken: null,
      listeningScore: '',
      readingScore: '',
      writingScore: '',
      speakingScore: '',
      vocabularyScore: '',
      totalScore: '',
      otherTestName: '',
    });
    this.englishProficiencyFormGroup.updateValueAndValidity();

    // this.configureJquery();
  }


  updateAllSubjectsTaughtInEnglish(allSubjectsTaughtInEnglish: boolean) {
    this.allSubjectsTaughtInEnglish = allSubjectsTaughtInEnglish;
    this.englishProficiencyFormGroup.patchValue({
      allSubjectsTaughtInEnglish: allSubjectsTaughtInEnglish
    });
    this.englishProficiencyFormGroup.updateValueAndValidity();
  }

  updateSpeaksEnglishAtHome(speaksEnglishAtHome: boolean) {
    this.speaksEnglishAtHome = speaksEnglishAtHome;
    this.englishProficiencyFormGroup.patchValue({
      speaksEnglishAtHome: speaksEnglishAtHome
    });
    this.englishProficiencyFormGroup.updateValueAndValidity();
  }

  updateHasTakenTest(hasTakenTest: boolean) {
    this.hasTakenTest = hasTakenTest;

    if (!hasTakenTest) {
      this.testTypeCode = '';
    }

    this.englishProficiencyFormGroup.patchValue({
      hasTakenTest: hasTakenTest,
      testTypeCode: this.testTypeCode
    });
    this.englishProficiencyFormGroup.updateValueAndValidity();
  }

  getSubText(sectionIndex: number): string {
    let subText = '';

    switch (sectionIndex) {
      case 1:
        subText = this.hasSelectedEnglishTest() ? '1 of 3' : '1 of 2';
        break;
      case 2:
        subText = '2 of 3';
        break;
      case 3:
        subText = this.hasSelectedEnglishTest() ? '3 of 3' : '2 of 2';
        break;
    }

    return subText;
  }

  hasSelectedEnglishTest(): boolean {
    return this.hasTakenTest && this.testTypeCode !== null && this.testTypeCode !== '';
  }



  setApiValidationError(apiErrorKeyName: string, apiErrorMsg): void {
    const errorField = this.errorKeyFormFieldMapping.find(errorKeyFormField => errorKeyFormField.apiErrorKeyName === apiErrorKeyName);

    if (errorField) {
      const { formFieldName } = errorField;
      this.englishProficiencyFormGroup.get(formFieldName).setErrors({ apiValidationError: true });
      errorField.apiValidationErrorMsg = apiErrorMsg;
    }

  }

  getApiValidationErrorMsg(formFieldName: string): string {
    const errorField = this.errorKeyFormFieldMapping.find(errorKeyFormField => errorKeyFormField.formFieldName === formFieldName);
    return errorField.apiValidationErrorMsg ? errorField.apiValidationErrorMsg : '';
  }

  previous() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'student-details']);
  }
  next() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'school-program-placement']);
  }

  getDateFromString(dateInString: string): Date {
    const dateParts = dateInString.split('/');
    return new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
  }


  updateFormValuesFromEnglishProficiency(englishProficiencyDetails: EnglishProficiencyDetails): void {

    //console.log(englishProficiencyDetails);
    this.englishProficiencyFormGroup.patchValue({
      testTypeCode: englishProficiencyDetails.TestTypeCode,
      hasTakenTest: englishProficiencyDetails.HasTakenTest,
      dateTaken: englishProficiencyDetails.DateTaken ? new Date(englishProficiencyDetails.DateTaken) : null,
      listeningScore: englishProficiencyDetails.ListeningScore,
      readingScore: englishProficiencyDetails.ReadingScore,
      writingScore: englishProficiencyDetails.WritingScore,
      speakingScore: englishProficiencyDetails.SpeakingScore,
      vocabularyScore: englishProficiencyDetails.VocabularyScore,
      totalScore: englishProficiencyDetails.TotalScore,
      otherTestName: englishProficiencyDetails.OtherTestName,
      allSubjectsTaughtInEnglish: englishProficiencyDetails.AllSubjectsTaughtInEnglish,
      speaksEnglishAtHome: englishProficiencyDetails.SpeaksEnglishAtHome
    });

    this.hasTakenTest = englishProficiencyDetails.HasTakenTest;
    this.allSubjectsTaughtInEnglish = englishProficiencyDetails.AllSubjectsTaughtInEnglish;
    this.speaksEnglishAtHome = englishProficiencyDetails.SpeaksEnglishAtHome;
    this.testTypeCode = englishProficiencyDetails.TestTypeCode;

    this.englishProficiencyFormGroup.updateValueAndValidity();

  }

  getDate(fieldName: string): string {
    const dateObject = this.englishProficiencyFormGroup.get(fieldName).value;
    const dateTimeObject = DateTime.fromJSDate(dateObject);

    if (!dateTimeObject.isValid) {
      return null;
    }

    const initialDate = new Date(1970, 0, 1);

    if (dateObject.getDate() === initialDate.getDate() &&
      dateObject.getMonth() === initialDate.getMonth() &&
      dateObject.getFullYear() === initialDate.getFullYear()) {
      return null;
    }
    const userTimezoneOffset = dateObject.getTimezoneOffset() * 60000;
    const date = new Date(dateObject.getTime() - userTimezoneOffset);
    return date.toISOString();
  }

  convertDateToString(dateInString: string): string {
    if (!dateInString) {
      return null;
    }

    const date = new Date(dateInString);
    const dateStr = date ? `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}` : '';
    return dateStr;
  }

  getEnglishProficiencyDetails(): EnglishProficiencyDetails {

    const englishProficiencyDetails = new EnglishProficiencyDetails();
    englishProficiencyDetails.HasTakenTest = this.englishProficiencyFormGroup.get('hasTakenTest').value;

    if (englishProficiencyDetails.HasTakenTest) {
      englishProficiencyDetails.TestTypeCode = this.englishProficiencyFormGroup.get('testTypeCode').value;
      // englishProficiencyDetails.DateTaken = this.englishProficiencyFormGroup.get('dateTaken').value;
      englishProficiencyDetails.DateTaken =  this.utilityService.getDateStrWithoutTimeZone(this.englishProficiencyFormGroup.get('dateTaken').value) //this.getDate('dateTaken')
      englishProficiencyDetails.ListeningScore = this.englishProficiencyFormGroup.get('listeningScore').value;
      englishProficiencyDetails.ReadingScore = this.englishProficiencyFormGroup.get('readingScore').value;
      englishProficiencyDetails.WritingScore = this.englishProficiencyFormGroup.get('writingScore').value;
      englishProficiencyDetails.SpeakingScore = this.englishProficiencyFormGroup.get('speakingScore').value;
      englishProficiencyDetails.VocabularyScore = this.englishProficiencyFormGroup.get('vocabularyScore').value;
      englishProficiencyDetails.TotalScore = this.englishProficiencyFormGroup.get('totalScore').value;
      englishProficiencyDetails.OtherTestName = this.englishProficiencyFormGroup.get('otherTestName').value;
    }
    englishProficiencyDetails.AllSubjectsTaughtInEnglish = this.englishProficiencyFormGroup.get('allSubjectsTaughtInEnglish').value;
    englishProficiencyDetails.SpeaksEnglishAtHome = this.englishProficiencyFormGroup.get('speaksEnglishAtHome').value;

    return englishProficiencyDetails;
  }

  saveAndContinue() {
    this.isFormSubmitted = true;

    const englishProficiencyDetails = this.getEnglishProficiencyDetails();
    this.applicationService.updateEnglishProficiencyDetails(this.applicationId, englishProficiencyDetails)
      .subscribe((response: SingleResultApiResponse) => {
        this.dispatchSectionSaveAction();
        this.isSaveSuccessful = true;
        this.router.navigate(['/applications', this.applicationId, 'edit', 'school-program-placement']);
      }, (errorResponse: ErrorResponse) => {
        const { errors } = errorResponse;
        for (const error of errors) {
          this.setApiValidationError(error.Key, error.Value);
        }
        this.englishProficiencyFormGroup.updateValueAndValidity();
        this.dispatchSectionSaveAction();
        this.initialEnglishProficiencyDetails = this.getEnglishProficiencyDetails();
        this.isScrollDone = false;
        this.formService.scrollToFirstErrorField();
      });
  }

  ngAfterContentChecked(): void {
    if (!this.isScrollDone) {
      this.isScrollDone = this.formService.scrollToFirstErrorField();
    }
  }

  dispatchSectionSaveAction() {
    this.store.dispatch({
      type: Action_ApplicationSectionSaved
    });
  }

  canDeactivate(): Observable<boolean> | boolean {

    if (this.isSaveSuccessful) {
      return true;
    }

    if (!this.isEnglishProficiencyDetailsSame()) {
      this.modalService.showModal();
      return this.modalService.stayOnThePage;
    } else {
      this.modalService.hideModal();
      return true;
    }
  }


  isEnglishProficiencyDetailsSame(): boolean {

    const currentEnglishProficiencyDetails = this.getEnglishProficiencyDetails();
    const initialEnglishProficiencyDetails = this.initialEnglishProficiencyDetails;

    const isEnglishProficiencyDetailsSame =
      compareEnglishProficiencyDetails(initialEnglishProficiencyDetails, currentEnglishProficiencyDetails);

    return isEnglishProficiencyDetailsSame;
  }

  //accessibility -- link error msg to form field
  errorDoubleErrorAccessibility(formControlName: string, requireErrorId: string, apiErrorId: string): string {
    if (this.isFormSubmitted &&
      this.englishProficiencyFormGroup.get(formControlName).errors &&
      this.englishProficiencyFormGroup.get(formControlName).errors.required) {
      return requireErrorId;
    } else if (this.isFormSubmitted &&
      this.englishProficiencyFormGroup.get(formControlName).errors &&
      this.englishProficiencyFormGroup.get(formControlName).errors.apiValidationError) {
      return apiErrorId;
    } else {
      return "";
    }
  }

}
